import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@/store/authStore';
import type { AssetNotification } from '@/services/notificationsService';

interface NotificationSocketEvents {
  onNotification?: (notification: AssetNotification) => void;
  onUnreadCount?: (count: number) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
}

export function useNotificationSocket(events: NotificationSocketEvents = {}) {
  const socketRef = useRef<Socket | null>(null);
  const { getToken, user } = useAuthStore();
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const connect = useCallback(async () => {
    if (!user) {
      return;
    }

    const token = await getToken();
    if (!token) {
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current?.connected) {
      socketRef.current.disconnect();
    }

    // "/notifications" here is the Socket.IO *namespace*, not a URL path —
    // the actual handshake always goes to /socket.io (proxied with ws: true
    // in vite.config.ts, pointing at notification-service). Passing a bare
    // namespace resolves against the page's own origin, so no manual
    // origin/protocol computation is needed, same as every other /api/* call.
    socketRef.current = io('/notifications', {
      auth: {
        token,
      },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 3,
      autoConnect: true,
    });

    // Connection event handlers
    socketRef.current.on('connect', () => {
      console.log('[NotificationSocket] Connected');
      eventsRef.current.onConnect?.();
    });

    socketRef.current.on('disconnect', (reason: string) => {
      console.log('[NotificationSocket] Disconnected:', reason);
      eventsRef.current.onDisconnect?.();
    });

    socketRef.current.on('connect_error', (error: Error) => {
      eventsRef.current.onError?.(error);
    });

    // Notification event handlers
    socketRef.current.on('notification', (notification: AssetNotification) => {
      console.log('[NotificationSocket] New notification:', notification);
      eventsRef.current.onNotification?.(notification);
    });

    socketRef.current.on('unread-count', (data: { unreadCount: number }) => {
      console.log('[NotificationSocket] Unread count update:', data.unreadCount);
      eventsRef.current.onUnreadCount?.(data.unreadCount);
    });

    // Pong handler for health check
    socketRef.current.on('pong', (data: { timestamp: number }) => {
      console.log('[NotificationSocket] Pong received:', data.timestamp);
    });

  }, [getToken, user]);

  // Connect on mount and when user changes
  useEffect(() => {
    if (user) {
      void connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [connect, user]);

  // Send ping to keep connection alive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (socketRef.current?.connected) {
        socketRef.current.emit('ping');
      }
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(pingInterval);
  }, []);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  const markAsRead = useCallback((notificationId: number) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('mark-read', { notificationId });
    }
  }, []);

  return {
    socket: socketRef.current,
    isConnected: socketRef.current?.connected ?? false,
    disconnect,
    markAsRead,
  };
}
